import React, { useState } from "react";
import {
  Calendar,
  Clock,
  Flag,
  Gavel,
  Layers,
  Tag,
  X,
} from "lucide-react";
import {
  formatLineDate,
  getMarketScheduleRows,
  getMultiLineCounts,
  getResolutionRows,
} from "../../../utils/bondPredictDetailHelpers";
import {
  MARKET_TYPES,
  STATUS_META,
  TYPE_META,
} from "../../../utils/bondPredictHelpers";
import H2HMatchContext from "./H2HMatchContext";

const SCHEDULE_ICONS = {
  Opens: Calendar,
  Closes: Clock,
  "Result by": Flag,
};

const MetaRow = ({ icon: Icon, label, value, multiline = false }) => {
  if (!value) return null;
  return (
    <div className={`bp-detail-meta-row${multiline ? " bp-detail-meta-source" : ""}`}>
      <Icon size={11} strokeWidth={2.25} />
      <span>{label}</span>
      <strong title={typeof value === "string" ? value : undefined}>{value}</strong>
    </div>
  );
};

const CopyBlock = ({ label, children, clamp = false, open = false }) => (
  <div className="bp-detail-about-block">
    <p className="bp-detail-about-label">{label}</p>
    <div
      className={`bp-detail-about-text${
        clamp && !open ? " bp-detail-about-text-clamp" : ""
      }`}
    >
      {children}
    </div>
  </div>
);

const DockChip = ({ label, value, tone = "default", wide = false }) => {
  if (!value) return null;
  return (
    <span
      className={`bp-detail-about-dock-chip bp-detail-about-dock-chip-${tone}${
        wide ? " bp-detail-about-dock-chip-wide" : ""
      }`}
    >
      <em>{label}</em>
      <strong title={typeof value === "string" ? value : undefined}>{value}</strong>
    </span>
  );
};

const MarketDetailAboutDock = ({
  onClose,
  panelTitle,
  statusMeta,
  status,
  scheduleRows,
  lineCounts,
  resolutionRows,
  description,
  rules,
  source,
  fx,
  hasH2hFixture,
  homeName,
  awayName,
}) => {
  const homeLogo = fx?.teams?.home?.logo;
  const awayLogo = fx?.teams?.away?.logo;
  const league = fx?.league?.name;
  const homeGoals = fx?.goals?.home;
  const awayGoals = fx?.goals?.away;
  const hasScore =
    homeGoals != null && awayGoals != null && !Number.isNaN(Number(homeGoals));
  const kickoff = fx?.timestamp ? formatLineDate(fx.timestamp) : null;

  const resolutionFacts = resolutionRows
    .filter((row) => {
      const text = String(row.value || "").trim();
      return Boolean(text);
    })
    .map((row) => ({
      key: row.label,
      label: row.label,
      value: String(row.value).trim(),
      tone: row.label === "Outcome" ? "accent" : "default",
      wide: row.label === "Rationale" || String(row.value).length > 42,
    }));

  const secondaryLine = [rules && rules !== description ? rules : "", source]
    .filter(Boolean)
    .join(" · ");

  const factChips = [
    ...scheduleRows.map((row) => ({
      key: row.label,
      label: row.label,
      value: row.format(row.value),
    })),
    lineCounts
      ? {
          key: "lines",
          label: "Lines",
          value: `${lineCounts.live} live · ${lineCounts.total}`,
        }
      : null,
    ...resolutionFacts,
  ].filter(Boolean);

  return (
    <section
      className="bp-detail-panel bp-detail-panel-about bp-detail-panel-about-compact bp-detail-panel-about-dock"
      aria-label="Market context"
    >
      <div className="bp-detail-about-dock-head">
        <div className="bp-detail-about-dock-title">
          <strong>Context</strong>
          {panelTitle ? <span className="bp-detail-about-dock-type">{panelTitle}</span> : null}
        </div>
        <div className="bp-detail-about-dock-actions">
          {status !== "OPEN" ? (
            <span
              className={`bpd-chrome-status bpd-chrome-status-${statusMeta.tone}`}
              title={`Market status: ${statusMeta.label}`}
            >
              {statusMeta.label}
            </span>
          ) : null}
          {onClose ? (
            <button
              type="button"
              className="bp-detail-about-dock-close"
              onClick={onClose}
              aria-label="Close context"
              title="Close"
            >
              <X size={12} strokeWidth={2.4} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="bp-detail-about-dock-body">
        {factChips.length ? (
          <div className="bp-detail-about-dock-facts" aria-label="Market facts">
            {factChips.map((chip) => (
              <DockChip
                key={chip.key}
                label={chip.label}
                value={chip.value}
                tone={chip.tone}
                wide={chip.wide}
              />
            ))}
          </div>
        ) : null}

        {hasH2hFixture ? (
          <div className="bp-detail-about-dock-match">
            <div className="bp-detail-about-dock-team">
              {homeLogo ? <img src={homeLogo} alt="" /> : null}
              <span>{homeName}</span>
              {hasScore ? <em className="tabular-nums">{homeGoals}</em> : null}
            </div>
            <span className="bp-detail-about-dock-vs">vs</span>
            <div className="bp-detail-about-dock-team">
              {awayLogo ? <img src={awayLogo} alt="" /> : null}
              <span>{awayName}</span>
              {hasScore ? <em className="tabular-nums">{awayGoals}</em> : null}
            </div>
            {league || kickoff ? (
              <span className="bp-detail-about-dock-match-meta">
                {[league, kickoff].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </div>
        ) : null}

        {description ? (
          <p className="bp-detail-about-dock-lead" title={description}>
            {description}
          </p>
        ) : null}

        {secondaryLine ? (
          <p className="bp-detail-about-dock-sub" title={secondaryLine}>
            {secondaryLine}
          </p>
        ) : null}
      </div>
    </section>
  );
};

const MarketDetailAbout = ({ market, compact = false, onClose }) => {
  const [descOpen, setDescOpen] = useState(false);

  if (!market) return null;

  const type = market.type || MARKET_TYPES.BINARY;
  const typeMeta = TYPE_META[type] || TYPE_META.BINARY;
  const category = market.category?.replace(/_/g, " ");
  const status = market.status || "OPEN";
  const statusMeta = STATUS_META[status] || { label: status, tone: "muted" };
  const scheduleRows = getMarketScheduleRows(market);
  const resolutionRows = getResolutionRows(market);
  const description = (market.description || "").trim();
  const rules = (market.rules || "").trim();
  const source = (market.resolutionSource || "").trim();
  const isMulti = type === MARKET_TYPES.MULTI_BINARY;
  const lineCounts = isMulti ? getMultiLineCounts(market) : null;
  const fx = market.fixture;
  const homeName = fx?.teams?.home?.name;
  const awayName = fx?.teams?.away?.name;
  const hasH2hFixture = type === MARKET_TYPES.HEAD_TO_HEAD && homeName && awayName;

  const longDescription = description.length > (compact ? 120 : 220);
  const panelTitle = [typeMeta.short, category].filter(Boolean).join(" · ");

  const hasMetaRows =
    scheduleRows.length > 0 || lineCounts || resolutionRows.length > 0;

  const hasCopy = description || (rules && rules !== description) || source;
  const hasRichContent = hasH2hFixture || hasMetaRows || hasCopy;

  if (compact && !hasRichContent) return null;

  if (compact) {
    return (
      <MarketDetailAboutDock
        onClose={onClose}
        panelTitle={panelTitle}
        statusMeta={statusMeta}
        status={status}
        scheduleRows={scheduleRows}
        lineCounts={lineCounts}
        resolutionRows={resolutionRows}
        description={description}
        rules={rules}
        source={source}
        fx={fx}
        hasH2hFixture={hasH2hFixture}
        homeName={homeName}
        awayName={awayName}
      />
    );
  }

  return (
    <section
      className={`bp-detail-panel bp-detail-panel-about${compact ? " bp-detail-panel-about-compact" : ""}`}
      aria-label="Market context"
    >
      <div className="bp-detail-panel-head">
        <div>
          {!compact ? <p className="bp-detail-panel-kicker">Context</p> : null}
          <h2>{compact ? "Context" : panelTitle || "Market details"}</h2>
          {!compact ? (
            <p className="bp-detail-about-scope">
              Market-level rules and timing. Applies to every outcome in this market.
            </p>
          ) : null}
          {compact && panelTitle ? (
            <p className="bp-detail-panel-about-sub">{panelTitle}</p>
          ) : null}
        </div>
        {status !== "OPEN" ? (
          <span
            className={`bpd-chrome-status bpd-chrome-status-${statusMeta.tone}`}
            title={`Market status: ${statusMeta.label}`}
          >
            {statusMeta.label}
          </span>
        ) : null}
      </div>

      <div className="bp-detail-about-body" tabIndex={compact ? -1 : undefined}>
        {hasH2hFixture ? (
          <div className="bp-detail-about-fixture">
            <H2HMatchContext fixture={fx} variant="detail" />
          </div>
        ) : null}

        {hasMetaRows || !hasRichContent ? (
          <div className="bp-detail-meta-panel">
            {scheduleRows.length > 0 ? (
              <div className="bp-detail-meta-schedule">
                {scheduleRows.map((row) => {
                  const Icon = SCHEDULE_ICONS[row.label] || Calendar;
                  return (
                    <MetaRow
                      key={row.label}
                      icon={Icon}
                      label={row.label}
                      value={row.format(row.value)}
                    />
                  );
                })}
              </div>
            ) : null}
            {lineCounts ? (
              <MetaRow
                icon={Layers}
                label="Lines"
                value={`${lineCounts.live} live · ${lineCounts.total} total`}
              />
            ) : null}
            {resolutionRows.map((row) => (
              <MetaRow
                key={row.label}
                icon={Gavel}
                label={row.label}
                value={row.value}
                multiline={row.label === "Rationale"}
              />
            ))}
            {typeMeta?.short ? (
              <MetaRow icon={Tag} label="Type" value={typeMeta.short} />
            ) : null}
            {category ? (
              <MetaRow icon={Tag} label="Category" value={category} />
            ) : null}
            {!hasRichContent ? (
              <MetaRow icon={Gavel} label="Status" value={statusMeta.label} />
            ) : null}
          </div>
        ) : null}

        {!hasRichContent ? (
          <p className="bp-detail-about-empty">
            No extra rules, schedule, or resolution details were published for this market yet.
          </p>
        ) : null}

        {description ? (
          <CopyBlock label="Question" clamp={longDescription} open={descOpen}>
            <p>{description}</p>
            {longDescription ? (
              <button
                type="button"
                className="bp-detail-about-toggle"
                onClick={() => setDescOpen((v) => !v)}
              >
                {descOpen ? "Show less" : "Read more"}
              </button>
            ) : null}
          </CopyBlock>
        ) : null}

        {rules && rules !== description ? (
          <CopyBlock label="Rules">
            <p>{rules}</p>
          </CopyBlock>
        ) : null}

        {source ? (
          <CopyBlock label="Resolution source">
            <p>{source}</p>
          </CopyBlock>
        ) : null}
      </div>
    </section>
  );
};

export default MarketDetailAbout;
